/**
 * A faixa de envios da planilha de pendências corrigida (plano de
 * 2026-10-30), no topo de "Linhas com erro": o recibo é a própria página.
 * Fica gravada no servidor de propósito — recarregar a página não pode
 * apagar o único lugar onde as recusadas aparecem, e quem abre o upload
 * depois precisa saber que outra pessoa já enviou uma planilha.
 */

import { formatInteger } from "@/shared/utils/format";
import type { CorrectionSheetSubmission } from "@/shared/api/api-client";
import { parseServerTime } from "./upload-metrics";

const pad = (value: number) => String(value).padStart(2, "0");

/** "30/10 às 10:12", no relógio de quem olha (o carimbo é um instante). */
export function submissionMoment(value: string): string {
  const ms = parseServerTime(value);
  if (ms === null) return "";
  const date = new Date(ms);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} às ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Os desfechos do envio, sem os zerados; tudo zero é "nada a aplicar". */
export function submissionOutcomes(submission: CorrectionSheetSubmission): string {
  const plural = (count: number, one: string, many: string) =>
    `${formatInteger(count)} ${count === 1 ? one : many}`;
  const parts: string[] = [];
  if (submission.updated_count > 0) {
    parts.push(plural(submission.updated_count, "corrigida", "corrigidas"));
  }
  if (submission.discarded_count > 0) {
    parts.push(plural(submission.discarded_count, "descartada", "descartadas"));
  }
  if (submission.reprocessed_count > 0) {
    parts.push(
      `${plural(submission.reprocessed_count, "reprocessada", "reprocessadas")} sem mudança`
    );
  }
  if (submission.rejected_count > 0) {
    parts.push(plural(submission.rejected_count, "recusada", "recusadas"));
  }
  return parts.length > 0 ? parts.join(", ") : "nada a aplicar";
}

export function SheetSubmissions({
  submissions
}: {
  submissions: CorrectionSheetSubmission[];
}) {
  if (submissions.length === 0) return null;
  return (
    <ul className="sheet-submissions" aria-label="Planilhas corrigidas enviadas">
      {submissions.map(submission => {
        const more = submission.rejected_count - submission.rejected_lines.length;
        return (
          <li key={submission.id} className="sheet-submission">
            Planilha corrigida enviada em {submissionMoment(submission.submitted_at)} por{" "}
            {submission.submitted_by_name ?? submission.submitted_by} (
            <code>{submission.filename}</code>): {submissionOutcomes(submission)}
            {submission.rejected_count > 0 && (
              <details>
                <summary>
                  ver {submission.rejected_count === 1 ? "a recusada" : `as ${formatInteger(submission.rejected_count)} recusadas`}
                </summary>
                <ul>
                  {submission.rejected_lines.map((line, index) => (
                    <li key={`${line.row_index}-${index}`}>
                      {/* Mesmo número da coluna Linha da planilha e da tabela "Linhas com erro". */}
                      {line.row_index >= 0 ? `Linha ${line.row_index + 1} — ` : ""}
                      {line.reason}
                    </li>
                  ))}
                  {more > 0 && <li>e mais {formatInteger(more)}</li>}
                </ul>
              </details>
            )}
          </li>
        );
      })}
    </ul>
  );
}
